import { useMemo, useEffect, useRef } from 'react';
import { getAgeRangeLabel } from '../utils/client.utils';
import { generateCheerfulMessage } from '../utils/cheerful-messages.utils';

/**
 * Feature 060: Removed miles parameter
 */
type UseMatchmakingComputedProps = {
  selectedClient: { profile?: any; avatar?: string } | null;
  selectedZipCode: string;
  selectedCounty: string;
  isViewingHistoryResults: boolean;
  searchCounter: number;
  isLoading: boolean;
  hasMore: boolean;
  loadCount: number;
  apiResults: any[];
};

export const useMatchmakingComputed = ({
  selectedClient,
  selectedZipCode,
  selectedCounty,
  isViewingHistoryResults,
  searchCounter,
  isLoading,
  hasMore,
  loadCount,
  apiResults,
}: UseMatchmakingComputedProps) => {
  const clientProfile = selectedClient?.profile;
  const ageDisplay = clientProfile
    ? typeof clientProfile.age === 'number'
      ? getAgeRangeLabel(clientProfile.age)
      : clientProfile.ageRange ?? '-'
    : '-';

  const matchResults = apiResults;
  const hasMatches = matchResults.length > 0;
  const shouldShowPostSingleLoadExhaustedMessage =
    !isViewingHistoryResults && !isLoading && !hasMore && loadCount === 2;
  const shouldShowPostSecondLoadMessage =
    !isViewingHistoryResults && !isLoading && loadCount >= 3;

  const cheerfulMessage = useMemo(
    () =>
      generateCheerfulMessage({
        zipCode: selectedZipCode,
        county: selectedCounty,
        isViewingHistoryResults,
        searchCounter,
      }),
    [searchCounter, selectedZipCode, selectedCounty, isViewingHistoryResults],
  );

  return {
    clientProfile,
    ageDisplay,
    matchResults,
    hasMatches,
    shouldShowPostSingleLoadExhaustedMessage,
    shouldShowPostSecondLoadMessage,
    cheerfulMessage,
  };
};

export const useHistoryFetchEffect = (
  advisorProfileId: string | null,
  selectedClientId: string | null,
  setShouldFetchHistory: (updater: (prev: boolean) => boolean) => void,
) => {
  const lastHistoryClientIdRef = useRef<string | null>(null);
  const historyFetchedForClientRef = useRef<string | null>(null);

  useEffect(() => {
    if (advisorProfileId && selectedClientId) {
      const clientKey = `${advisorProfileId}-${selectedClientId}`;
      if (historyFetchedForClientRef.current !== clientKey) {
        historyFetchedForClientRef.current = clientKey;
        lastHistoryClientIdRef.current = selectedClientId;
        setShouldFetchHistory((prev) => (prev ? prev : true));
      }
    } else {
      historyFetchedForClientRef.current = '';
    }
  }, [advisorProfileId, selectedClientId, setShouldFetchHistory]);

  return { lastHistoryClientIdRef };
};

